/**
 * Client-side 3-D wing-shell builder (FXC-9009 — real-time engine for WingViewer3D).
 *
 * Takes a HighLiftAirfoil + planform + flap band and builds the 3-D flapped-wing
 * geometry ENTIRELY in the browser, with no backend round-trip:
 *
 *   buildSweepInputs(airfoil)          -> deployed 2-D contours (main / vane / flap)
 *   loft each element across the span  -> watertight element solids
 *   manifold boolean: (clean - band) ∪ cove  -> the cove pocket cut into the wing
 *
 * Mirrors the verified Python pipeline (app/services/wing/shell_loft.py + the
 * trimesh/manifold cutout) so the live preview and the Flow360 export agree. The
 * only runtime dep beyond the existing in-browser 2-D engine is `manifold-3d`
 * (WASM); pass an initialised module in (so the WASM is loaded once by the caller).
 *
 * Pure geometry: returns indexed triangle meshes per element (positions + indices)
 * ready for THREE.BufferGeometry. No three / DOM imports here.
 */
import { buildSweepInputs } from './sweepPayload';
import type { HighLiftAirfoil } from './estolConfig';

export type V2 = [number, number];

export interface WingPlanform3D {
  rootChord: number;
  tipChord: number;
  rootLeX: number;
  tipLeX: number;
  span: number;          // half-span (root at y=0 on the symmetry plane)
  dihedralDeg?: number;
  twistTipDeg?: number;  // linear washout root(0)->tip
}

export interface ElementMesh {
  name: string;          // 'wing' | 'vane' | 'flap'
  positions: Float32Array;  // flat xyz
  indices: Uint32Array;
}

export interface WingShell {
  elements: ElementMesh[];
}

/** Minimal subset of the manifold-3d module surface this file needs. */
export interface ManifoldModule {
  Manifold: any;
  Mesh: any;
}

// ---------------------------------------------------------------------------
// 2-D loop helpers (port of shell_loft.resample_loop / _ear_clip)
// ---------------------------------------------------------------------------

function closeLoop(p: V2[]): V2[] {
  if (p.length < 2) return p;
  const a = p[0]!, b = p[p.length - 1]!;
  if (Math.abs(a[0] - b[0]) > 1e-9 || Math.abs(a[1] - b[1]) > 1e-9) return [...p, a];
  return p;
}

/** Arc-length-uniform resample of a closed polygon to n points (open ring). */
export function resampleLoop(loop: V2[], n: number): V2[] {
  const P = closeLoop(loop);
  const s = [0];
  for (let i = 1; i < P.length; i++) s.push(s[i - 1]! + Math.hypot(P[i]![0] - P[i - 1]![0], P[i]![1] - P[i - 1]![1]));
  const total = s[s.length - 1]! || 1;
  const out: V2[] = [];
  for (let i = 0; i < n; i++) {
    const t = (total * i) / n;
    let j = 0;
    while (j < s.length - 2 && s[j + 1]! < t) j++;
    const f = s[j + 1]! > s[j]! ? (t - s[j]!) / (s[j + 1]! - s[j]!) : 0;
    out.push([P[j]![0] + f * (P[j + 1]![0] - P[j]![0]), P[j]![1] + f * (P[j + 1]![1] - P[j]![1])]);
  }
  return out;
}

/** Ear-clip triangulation of a simple (possibly non-convex) loop -> index triples. */
export function earClip(poly: V2[]): [number, number, number][] {
  const n = poly.length;
  let idx = Array.from({ length: n }, (_, i) => i);
  let area = 0;
  for (let i = 0; i < n; i++) area += poly[i]![0] * poly[(i + 1) % n]![1] - poly[(i + 1) % n]![0] * poly[i]![1];
  if (area < 0) idx.reverse();
  const cross = (o: V2, a: V2, b: V2) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const inTri = (p: V2, a: V2, b: V2, c: V2) => {
    const d1 = cross(a, b, p), d2 = cross(b, c, p), d3 = cross(c, a, p);
    return !(((d1 < 0) || (d2 < 0) || (d3 < 0)) && ((d1 > 0) || (d2 > 0) || (d3 > 0)));
  };
  const tris: [number, number, number][] = [];
  let guard = 0;
  while (idx.length > 3 && guard++ < 20000) {
    let eared = false;
    const m = idx.length;
    for (let ii = 0; ii < m; ii++) {
      const i0 = idx[(ii - 1 + m) % m]!, i1 = idx[ii]!, i2 = idx[(ii + 1) % m]!;
      const a = poly[i0]!, b = poly[i1]!, c = poly[i2]!;
      if (cross(a, b, c) <= 0) continue;
      if (idx.some((k) => k !== i0 && k !== i1 && k !== i2 && inTri(poly[k]!, a, b, c))) continue;
      tris.push([i0, i1, i2]); idx.splice(ii, 1); eared = true; break;
    }
    if (!eared) { for (let ii = 1; ii < idx.length - 1; ii++) tris.push([idx[0]!, idx[ii]!, idx[ii + 1]!]); return tris; }
  }
  if (idx.length === 3) tris.push([idx[0]!, idx[1]!, idx[2]!]);
  return tris;
}

// ---------------------------------------------------------------------------
// Placement + lofting
// ---------------------------------------------------------------------------

type V3 = [number, number, number];

function placeSection(loop: V2[], eta: number, p: WingPlanform3D): V3[] {
  const chord = p.rootChord + (p.tipChord - p.rootChord) * eta;
  const y = p.span * eta;
  const leX = p.rootLeX + (p.tipLeX - p.rootLeX) * eta;
  const dz = y * Math.tan(((p.dihedralDeg ?? 0) * Math.PI) / 180);
  const tw = ((p.twistTipDeg ?? 0) * eta * Math.PI) / 180;
  const c = Math.cos(tw), s = Math.sin(tw), px = 0.25 * chord;
  return loop.map(([lx, lz]) => {
    const X = lx * chord, Z = lz * chord;
    const xr = px + (X - px) * c + Z * s;
    const zr = -(X - px) * s + Z * c;
    return [leX + xr, y, dz + zr] as V3;
  });
}

/** Signed area (shoelace) of a 2-D loop in the (lx, lz) plane; > 0 == CCW. */
function loopSignedArea(loop: V2[]): number {
  let a = 0;
  for (let i = 0; i < loop.length; i++) {
    const p = loop[i]!, q = loop[(i + 1) % loop.length]!;
    a += p[0] * q[1] - q[0] * p[1];
  }
  return a / 2;
}

/** Loft a constant-N loop across span stations into an INDEXED watertight mesh.
 *
 *  The loop is first normalised to CCW so the ruled side walls and the ear-clipped
 *  end caps share ONE consistent winding — manifold-3d checks *oriented*
 *  manifoldness and rejects mixed winding as "Not manifold" (the cove/vane/flap
 *  sweep contours are already CCW, but the clean-wing loop from `cleanLoopFrom`
 *  is CW, so without this it threw). The global winding is then flipped so the
 *  solid has OUTWARD normals (positive volume) — needed for the manifold boolean
 *  ops to behave as solids and for correct shading. */
function loftIndexed(loop: V2[], etas: number[], plan: WingPlanform3D): { pos: number[]; idx: number[] } {
  const ccw = loopSignedArea(loop) < 0 ? [...loop].reverse() : loop;
  const M = ccw.length;
  const rings = etas.map((e) => placeSection(ccw, e, plan));
  const pos: number[] = [];
  for (const r of rings) for (const v of r) pos.push(v[0], v[1], v[2]);
  const idx: number[] = [];
  const NS = rings.length;
  for (let s = 0; s < NS - 1; s++) {
    for (let k = 0; k < M; k++) {
      const k1 = (k + 1) % M;
      const a = s * M + k, b = s * M + k1, cc = (s + 1) * M + k1, d = (s + 1) * M + k;
      idx.push(a, b, cc, a, cc, d);
    }
  }
  // caps (ear-clipped) at root + tip
  const cap = earClip(ccw);
  const tipBase = (NS - 1) * M;
  for (const [i0, i1, i2] of cap) {
    idx.push(i0, i2, i1);                       // root cap (reversed)
    idx.push(tipBase + i0, tipBase + i1, tipBase + i2);  // tip cap
  }
  // Flip global winding -> outward normals (positive volume); stays manifold.
  for (let i = 0; i < idx.length; i += 3) { const t = idx[i + 1]!; idx[i + 1] = idx[i + 2]!; idx[i + 2] = t; }
  return { pos, idx };
}

/** Weld coincident vertices so the mesh is manifold (required by the boolean).
 *  Returns a `MeshOptions`-shaped object (`numProp: 3` — pure xyz positions). */
function weld(pos: number[], idx: number[], eps = 1e-6): { numProp: number; vertProperties: Float32Array; triVerts: Uint32Array } {
  const map = new Map<string, number>();
  const out: number[] = [];
  const remap = new Int32Array(pos.length / 3);
  const q = (v: number) => Math.round(v / eps);
  for (let i = 0; i < pos.length; i += 3) {
    const key = `${q(pos[i]!)},${q(pos[i + 1]!)},${q(pos[i + 2]!)}`;
    let id = map.get(key);
    if (id === undefined) { id = out.length / 3; map.set(key, id); out.push(pos[i]!, pos[i + 1]!, pos[i + 2]!); }
    remap[i / 3] = id;
  }
  return { numProp: 3, vertProperties: new Float32Array(out), triVerts: new Uint32Array(idx.map((i) => remap[i]!)) };
}

// ---------------------------------------------------------------------------
// Build the cutout shell
// ---------------------------------------------------------------------------

export interface BuildOpts {
  band?: [number, number];   // flap span band (eta), default [0.25, 0.70]
  nMain?: number;            // chordwise resample of the wing/cove loop
  nVf?: number;              // chordwise resample of vane/flap
  nSpan?: number;            // spanwise stations
}

/** Build a clean wing's loop from the base airfoil coords (closed, blunt TE). */
function cleanLoopFrom(airfoil: HighLiftAirfoil, nx = 130): V2[] {
  const up = [...airfoil.main.coords.upper].sort((a, b) => a[0] - b[0]);
  const lo = [...airfoil.main.coords.lower].sort((a, b) => a[0] - b[0]);
  const interp = (xs: V2[], x: number) => {
    if (x <= xs[0]![0]) return xs[0]![1];
    if (x >= xs[xs.length - 1]![0]) return xs[xs.length - 1]![1];
    let i = 1; while (i < xs.length && xs[i]![0] < x) i++;
    const [x0, y0] = xs[i - 1]!, [x1, y1] = xs[i]!;
    return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
  };
  const xs: number[] = [];
  for (let i = 0; i < nx; i++) xs.push(((1 - Math.cos((Math.PI * i) / (nx - 1))) / 2) * 0.99);
  const loop: V2[] = [];
  for (const x of xs) loop.push([x, interp(up, x)]);
  for (let i = xs.length - 2; i >= 1; i--) loop.push([xs[i]!, interp(lo, xs[i]!)]);
  return loop;
}

function meshToElement(name: string, mesh: { vertProperties: Float32Array; triVerts: Uint32Array }): ElementMesh {
  return { name, positions: new Float32Array(mesh.vertProperties), indices: new Uint32Array(mesh.triVerts) };
}

/**
 * Build the full flapped-wing shell (wing-with-cove-pocket + vane + flap).
 * `manifold` is an initialised manifold-3d module (caller loads the WASM once).
 */
export function buildWingShell(
  airfoil: HighLiftAirfoil,
  plan: WingPlanform3D,
  manifold: ManifoldModule,
  opts: BuildOpts = {},
): WingShell {
  const band = opts.band ?? [0.25, 0.70];
  const nMain = opts.nMain ?? 180, nVf = opts.nVf ?? 110, nSpan = opts.nSpan ?? 56;
  const { Manifold, Mesh } = manifold;

  const sweep = buildSweepInputs(airfoil);
  const byName = (n: string) => sweep.elements.find((e) => e.name === n)!.contour as V2[];
  const coveLoop = resampleLoop(byName('main'), nMain);
  const vaneLoop = resampleLoop(byName('vane'), nVf);
  const flapLoop = resampleLoop(byName('flap'), nVf);
  const cleanLoop = resampleLoop(cleanLoopFrom(airfoil), nMain);

  const etasFull = Array.from({ length: nSpan }, (_, i) => (1 - Math.cos((Math.PI * i) / (nSpan - 1))) / 2);
  const bandEtas = Array.from({ length: 28 }, (_, i) => band[0] + ((band[1] - band[0]) * i) / 27);

  const toManifold = (loop: V2[], etas: number[]) => {
    const { pos, idx } = loftIndexed(loop, etas, plan);
    return new Manifold(new Mesh(weld(pos, idx)));
  };

  const cleanWing = toManifold(cleanLoop, etasFull);
  const coveBand = toManifold(coveLoop, bandEtas);
  const vaneBand = toManifold(vaneLoop, bandEtas);
  const flapBand = toManifold(flapLoop, bandEtas);

  // band slab covering [y1,y2] over the full bbox
  const bb = cleanWing.boundingBox();
  const y1 = plan.span * band[0], y2 = plan.span * band[1];
  const sx = bb.max[0] - bb.min[0] + 2, sz = bb.max[2] - bb.min[2] + 2;
  const slab = Manifold.cube([sx, y2 - y1, sz], true).translate([
    (bb.min[0] + bb.max[0]) / 2, (y1 + y2) / 2, (bb.min[2] + bb.max[2]) / 2,
  ]);

  const wing = cleanWing.subtract(slab).add(coveBand);

  const out: ElementMesh[] = [
    meshToElement('wing', wing.getMesh()),
    meshToElement('vane', vaneBand.getMesh()),
    meshToElement('flap', flapBand.getMesh()),
  ];
  // free intermediate manifolds (wasm heap)
  [cleanWing, coveBand, vaneBand, flapBand, slab, wing].forEach((m) => m.delete?.());
  return { elements: out };
}
